import {AffiliateSubType, ShoppingType} from '~/gql/generated';
import {FC, useCallback, useEffect, useRef, useState} from 'react';

import {AddToCartButton} from '~/modules/Cart/components/AddToCartButton';
import {AnalyticsEvent} from '~/modules/Common/hooks/analyticsEvent';
import {Button} from '~/modules/Common/components/Button';
import {ButtonShapes} from '~/modules/Appearance/types';
import {MergedProduct} from '../context/types';
import {ProductOptions} from './ProductOptions';
import {QuantityInput} from './QuantityInput';
import {ShopifyAnalyticsProduct} from '@shopify/hydrogen';
import {Text} from '~/modules/Common/components/Text';
import {getDomainFromLink} from '~/modules/Common/util';
import {loader} from '~/routes/($expertName)/products/$productHandle.($productId)';
import {useExpert} from '~/modules/Common/hooks/useExpert';
import {useIntersection} from 'react-use';
import {useLoaderData} from '@remix-run/react';
import {useProduct} from '../hooks/useProduct';
import {useRudderStack} from '~/modules/Common/hooks/useRudderstack';
import {useSelectedVariant} from '../hooks/useSelectedVariant';

export function ProductForm() {
  const {analytics} = useLoaderData<typeof loader>();
  const product = useProduct();
  const [quantity] = useState(product.setSize || 1); // quantity can be set in the cart
  const staticButton = useRef(null);
  const intersection = useIntersection(staticButton, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });
  const [isFixedVisible, setIsFixedVisible] = useState(false);

  useEffect(() => {
    if (
      !isFixedVisible &&
      intersection &&
      !intersection?.isIntersecting &&
      intersection.boundingClientRect.y < 0
    ) {
      setIsFixedVisible(true);
    } else if (isFixedVisible && intersection && intersection?.isIntersecting) {
      setIsFixedVisible(false);
    }
  }, [intersection, isFixedVisible]);

  const productAnalytics: ShopifyAnalyticsProduct = {
    ...analytics?.products[0],
    quantity,
  };
  if (!product) {
    return null;
  }

  return (
    <>
      <div className="grid gap-4" ref={staticButton} id={'productForm'}>
        <ProductOptions product={product} />
        <ProductFormButton
          quantity={quantity}
          productAnalytics={productAnalytics}
          product={product}
        />
      </div>
      {isFixedVisible && (
        <div
          className={`md:hidden fixed bottom-0 left-0 right-0 bg-white p-3  z-[2147483002] shadow-bottomFixed`}
        >
          <ProductFormButton
            quantity={quantity}
            productAnalytics={productAnalytics}
            product={product}
          />
        </div>
      )}
    </>
  );
}

interface ProductFormButtonProps {
  quantity: number;
  productAnalytics?: ShopifyAnalyticsProduct;
  product: MergedProduct;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  setQuantity?: (quantity: number) => void;
  isQuantityVisible?: boolean;
  shape?: ButtonShapes;
  textColor?: string;
  backgroundColor?: string;
}

const outlinkTypes = [ShoppingType.Affiliate, ShoppingType.Partial];
const ProductFormButton: FC<ProductFormButtonProps> = ({product, ...props}) => {
  const {trackEvent} = useRudderStack();
  const {expertName, theme} = useExpert();
  const onClick = useCallback(() => {
    const eventType =
      product.shoppingType === ShoppingType.Affiliate
        ? AnalyticsEvent.affiliateProductClick
        : AnalyticsEvent.addToCart;
    trackEvent(eventType, {
      product,
      expertName,
    });
  }, [expertName, product, trackEvent]);

  return !outlinkTypes.includes(product.shoppingType) &&
    product.brandType !== AffiliateSubType.Amazon ? (
    <ProductAddToCartButton
      onClick={onClick}
      {...props}
      product={product}
      shape={theme?.v2?.buttonShape || ButtonShapes.rounded}
      textColor={theme?.v2?.buttonFontColor || ''}
      backgroundColor={theme?.v2?.buttonColor || ''}
    />
  ) : (
    <BuyNowButton
      product={product}
      onClick={onClick}
      shape={theme?.v2?.buttonShape || ButtonShapes.rounded}
      textColor={theme?.v2?.buttonFontColor || ''}
      backgroundColor={theme?.v2?.buttonColor || ''}
    />
  );
};

const BuyNowButton: FC<{
  product: MergedProduct;
  onClick: () => void;
  shape: ButtonShapes;
  textColor?: string;
  backgroundColor?: string;
}> = ({product, onClick, shape, textColor, backgroundColor}) => {
  const getLink = () => {
    const index = product.viewAllLinks?.findIndex(
      (item) => item?.linkType?.toLowerCase() === 'amazon',
    );

    if (Number(index) > -1 && product.viewAllLinks?.length) {
      return product.viewAllLinks[Number(index)]?.url;
    }
    return product.outlink;
  };

  const outlink = getLink() || product?.selectedVariant?.outlink || '';
  const isAmazon = outlink.includes('amazon.com');
  let copy = isAmazon ? 'Buy on Amazon' : 'Buy Now';

  if (product.affiliateSubType === AffiliateSubType.Impact) {
    copy = (
      <span>
        {`Buy on `}
        <span className="capitalize">
          {getDomainFromLink(outlink).split('.')[0]}
        </span>
      </span>
    );
  }
  return (
    <Button
      color="expert"
      fullSized
      href={outlink}
      target="_blank"
      onClick={onClick}
      shape={shape}
      textColor={textColor}
      backgroundColor={backgroundColor}
      className="justify-center"
    >
      {copy}
    </Button>
  );
};
const ProductAddToCartButton: FC<ProductFormButtonProps> = ({
  quantity,
  productAnalytics,
  product,
  onClick,
  setQuantity,
  isQuantityVisible = false,
  shape,
  textColor,
  backgroundColor,
}) => {
  const selectedVariant = useSelectedVariant(product);
  const isOutOfStock = !selectedVariant?.availableForSale;
  return (
    <div className="flex flex-row flex-nowrap gap-2 items-center">
      {isQuantityVisible && (
        <QuantityInput
          className={`flex-none flex basis-[88px] min-w-[1px]`}
          quantity={quantity}
          onQuantityChange={(nextQuantity) => {
            setQuantity && setQuantity(nextQuantity);
          }}
        />
      )}
      <AddToCartButton
        onClick={onClick}
        disabled={isOutOfStock}
        formClassName="w-full block"
        className="items-center justify-center text-center"
        lines={[
          {
            merchandiseId: selectedVariant?.id,
            quantity,
          },
        ]}
        variant={isOutOfStock ? 'secondary' : 'primary'}
        data-test="add-to-cart"
        analytics={{
          products: [productAnalytics],
          totalValue: parseFloat(productAnalytics.price),
        }}
        code={product.discount?.code || ''}
        shape={shape}
        textColor={textColor}
        backgroundColor={backgroundColor}
      >
        <Text as="span">{isOutOfStock ? 'Out Of Stock' : `Add to Cart`}</Text>
      </AddToCartButton>
    </div>
  );
};
